import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  Truck, Package, ShoppingCart, Utensils, Wrench, MapPin,
  RefreshCw, CheckCircle, Clock, Camera, PenTool, DollarSign,
  Navigation, Phone, User, ArrowRight, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryDriverDashboard = () => {
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [availableRes, myRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/delivery/available`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/delivery/my-deliveries`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/delivery/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (availableRes.ok) {
        const data = await availableRes.json();
        setAvailableDeliveries(data.available_deliveries || []);
      }

      if (myRes.ok) {
        const data = await myRes.json();
        setMyDeliveries(data.deliveries || []);
        
        // Find active delivery
        const active = data.deliveries?.find(d => 
          ['accepted', 'picked_up'].includes(d.status)
        );
        setActiveDelivery(active || null);
      }

      if (servicesRes.ok) {
        const data = await servicesRes.json();
        setServices(data.services || {});
      }
    } catch (error) {
      console.error('Failed to fetch delivery data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const acceptDelivery = async (deliveryId) => {
    try {
      const res = await fetch(`${API_URL}/api/delivery/accept/${deliveryId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        toast.success('Delivery accepted! Navigate to pickup location.');
        fetchData();
        setActiveTab('active');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to accept delivery');
      }
    } catch (error) {
      toast.error('Failed to accept delivery');
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'food': return <Utensils className="h-5 w-5" />;
      case 'parts': return <Wrench className="h-5 w-5" />;
      case 'groceries': return <ShoppingCart className="h-5 w-5" />;
      default: return <Package className="h-5 w-5" />;
    }
  };

  const getServiceColor = (type) => {
    switch (type) {
      case 'food': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'parts': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'groceries': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delivery-driver-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6 text-blue-600" />
            Delivery Dashboard
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Accept and complete delivery orders
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Services Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(services).map(([key, service]) => (
          <Card key={key} className={`border ${getServiceColor(key).split(' ')[2]}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${getServiceColor(key).split(' ').slice(0, 2).join(' ')}`}>
                  {getServiceIcon(key)}
                </div>
                <div>
                  <p className="font-medium text-sm">{service.name}</p>
                  <p className="text-xs text-gray-500">${service.base_fee} base</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Active Delivery Alert */}
      {activeDelivery && (
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  {getServiceIcon(activeDelivery.delivery_type)}
                </div>
                <div>
                  <p className="font-bold">Active Delivery</p>
                  <p className="text-sm text-blue-100">
                    {activeDelivery.service_name} - ${activeDelivery.total?.toFixed(2)}
                  </p>
                </div>
              </div>
              <Button 
                variant="secondary"
                onClick={() => setActiveTab('active')}
              >
                View Details
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'available' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('available')}
          className="flex items-center gap-2"
        >
          <Package className="h-4 w-4" />
          Available ({availableDeliveries.length})
        </Button>
        <Button
          variant={activeTab === 'active' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('active')}
          className="flex items-center gap-2"
        >
          <Truck className="h-4 w-4" />
          Active
        </Button>
        <Button
          variant={activeTab === 'history' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('history')}
          className="flex items-center gap-2"
        >
          <CheckCircle className="h-4 w-4" />
          History
        </Button>
      </div>

      {/* Available Deliveries */}
      {activeTab === 'available' && (
        <div className="space-y-4">
          {availableDeliveries.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">No available deliveries</p>
                <p className="text-sm">New orders will appear here</p>
              </CardContent>
            </Card>
          ) : (
            availableDeliveries.map((delivery) => (
              <Card key={delivery.delivery_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 rounded-lg ${getServiceColor(delivery.delivery_type)}`}>
                        {getServiceIcon(delivery.delivery_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{delivery.service_name}</p>
                          <Badge variant="outline">
                            {delivery.items?.length || 0} items
                          </Badge>
                        </div>
                        
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-500" />
                            <span className="truncate max-w-xs">Pickup: {delivery.pickup_address}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Navigation className="h-4 w-4 text-blue-500" />
                            <span className="truncate max-w-xs">Deliver: {delivery.delivery_address}</span>
                          </div>
                        </div>
                        
                        {delivery.notes && (
                          <p className="mt-2 text-sm bg-gray-50 p-2 rounded">
                            Note: {delivery.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          ${delivery.total?.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-400">
                          Delivery: ${delivery.delivery_fee?.toFixed(2)}
                        </p>
                      </div>
                      
                      <Button
                        onClick={() => acceptDelivery(delivery.delivery_id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`accept-delivery-${delivery.delivery_id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Accept Delivery
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Active Delivery Details */}
      {activeTab === 'active' && (
        <ActiveDeliveryPanel 
          delivery={activeDelivery} 
          onComplete={fetchData}
          token={token}
        />
      )}

      {/* Delivery History */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {myDeliveries.filter(d => d.status === 'completed').length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">No completed deliveries yet</p>
              </CardContent>
            </Card>
          ) : (
            myDeliveries
              .filter(d => d.status === 'completed')
              .map((delivery) => (
                <Card key={delivery.delivery_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getServiceColor(delivery.delivery_type)}`}>
                          {getServiceIcon(delivery.delivery_type)}
                        </div>
                        <div>
                          <p className="font-medium">{delivery.service_name}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(delivery.completed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">${delivery.total?.toFixed(2)}</p>
                        <Badge className="bg-green-100 text-green-800">Completed</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
          )}
        </div>
      )}
    </div>
  );
};


// Active Delivery Panel Component
const ActiveDeliveryPanel = ({ delivery, onComplete, token }) => {
  const [completionNotes, setCompletionNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!delivery) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <Truck className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="font-medium">No active delivery</p>
          <p className="text-sm">Accept a delivery to get started</p>
        </CardContent>
      </Card>
    );
  }

  const uploadPhotos = async (type) => {
    // In production, this would open a photo picker
    const mockPhotos = [`https://picsum.photos/400/300?random=${Date.now()}`];
    
    setProcessing(true);
    try {
      const endpoint = type === 'pickup' ? 'pickup-photos' : 'delivery-photos';
      const res = await fetch(`${API_URL}/api/delivery/${delivery.delivery_id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photos: mockPhotos })
      });

      if (res.ok) {
        toast.success(`${type === 'pickup' ? 'Pickup' : 'Delivery'} photos uploaded!`);
        onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload photos');
      }
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setProcessing(false);
    }
  };

  const captureSignature = async () => {
    // In production, this would open a signature pad
    const mockSignature = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/delivery/${delivery.delivery_id}/signature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          signature: mockSignature,
          signer_name: 'Customer'
        })
      });

      if (res.ok) {
        toast.success('Signature captured!');
        onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to capture signature');
      }
    } catch (error) {
      toast.error('Failed to capture signature');
    } finally {
      setProcessing(false);
    }
  };

  const completeDelivery = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/delivery/${delivery.delivery_id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: completionNotes })
      });

      if (res.ok) {
        toast.success('Delivery completed! Great job!');
        onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to complete delivery');
      }
    } catch (error) {
      toast.error('Failed to complete delivery');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Delivery Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Active Delivery
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Service</p>
              <p className="font-medium">{delivery.service_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="font-bold text-green-600">${delivery.total?.toFixed(2)}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-green-600 font-medium">PICKUP</p>
              <p className="font-medium">{delivery.pickup_address}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600 font-medium">DELIVER TO</p>
              <p className="font-medium">{delivery.delivery_address}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Completion Steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Pickup Photos */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                delivery.pickup_photos?.length > 0 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {delivery.pickup_photos?.length > 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Camera className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-medium">Pickup Photos</p>
                <p className="text-sm text-gray-500">Take photos at pickup location</p>
              </div>
            </div>
            <Button
              variant={delivery.pickup_photos?.length > 0 ? 'outline' : 'default'}
              size="sm"
              onClick={() => uploadPhotos('pickup')}
              disabled={processing}
            >
              <Camera className="h-4 w-4 mr-2" />
              {delivery.pickup_photos?.length > 0 ? 'Update' : 'Upload'}
            </Button>
          </div>

          {/* Step 2: Delivery Photos */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                delivery.delivery_photos?.length > 0 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {delivery.delivery_photos?.length > 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Camera className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-medium">Delivery Photos</p>
                <p className="text-sm text-gray-500">Take photos at delivery location</p>
              </div>
            </div>
            <Button
              variant={delivery.delivery_photos?.length > 0 ? 'outline' : 'default'}
              size="sm"
              onClick={() => uploadPhotos('delivery')}
              disabled={processing}
            >
              <Camera className="h-4 w-4 mr-2" />
              {delivery.delivery_photos?.length > 0 ? 'Update' : 'Upload'}
            </Button>
          </div>

          {/* Step 3: Customer Signature */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                delivery.customer_signature ? 'bg-green-100' : 'bg-yellow-100'
              }`}>
                {delivery.customer_signature ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <PenTool className="h-5 w-5 text-yellow-600" />
                )}
              </div>
              <div>
                <p className="font-medium">Customer Signature</p>
                <p className="text-sm text-gray-500">
                  {delivery.customer_signature ? 'Signature captured' : 'Required for completion'}
                </p>
              </div>
            </div>
            <Button
              variant={delivery.customer_signature ? 'outline' : 'default'}
              size="sm"
              onClick={captureSignature}
              disabled={processing}
              className={!delivery.customer_signature ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
            >
              <PenTool className="h-4 w-4 mr-2" />
              {delivery.customer_signature ? 'Update' : 'Capture'}
            </Button>
          </div>

          {/* Completion Notes */}
          <div className="pt-4 border-t">
            <label className="text-sm font-medium text-gray-700">Completion Notes (Optional)</label>
            <Textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Add any notes about the delivery..."
              rows={2}
              className="mt-1"
            />
          </div>

          {/* Complete Button */}
          <Button
            onClick={completeDelivery}
            disabled={processing || !delivery.customer_signature}
            className="w-full bg-green-600 hover:bg-green-700"
            size="lg"
          >
            {processing ? (
              <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-5 w-5 mr-2" />
            )}
            Complete Delivery
          </Button>

          {!delivery.customer_signature && (
            <p className="text-center text-sm text-yellow-600 flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Customer signature required to complete
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryDriverDashboard;
